/**
 * Minimal, safe Markdown subset for chat messages (Discord-like): ```code blocks```, `code`,
 * **bold**, *italic* / _italic_, ~~strike~~, bare links and task keys (`NEX-42`). Produces a token tree that React
 * renders as elements — no HTML is ever injected.
 */
export type MdNode =
  | { type: 'text'; text: string }
  | { type: 'link'; href: string; text: string }
  | { type: 'code'; text: string }
  | { type: 'codeblock'; text: string; lang: string | null }
  | { type: 'bold' | 'italic' | 'strike'; children: MdNode[] }
  | { type: 'mention'; userId: string | null }
  | { type: 'roleMention'; roleId: string }
  | { type: 'taskRef'; key: string; number: number };

/** `NEX-42`: a team's task key prefix and a task number. */
const TASK_REF = /(?<![\p{L}\p{N}_-])([A-Z][A-Z0-9]{1,5})-([1-9]\d{0,5})(?![\p{L}\p{N}_])/u;

const LINK = /\bhttps?:\/\/[^\s<>"'`]+[^\s<>"'`.,;:!?)\]*_~]/;

const INLINE: { type: 'bold' | 'strike' | 'italic'; pattern: RegExp }[] = [
  { type: 'bold', pattern: /\*\*(?=\S)([\s\S]*?\S)\*\*/ },
  { type: 'strike', pattern: /~~(?=\S)([\s\S]*?\S)~~/ },
  { type: 'italic', pattern: /(?<![\w*])\*(?=\S)([^*]*?\S)\*(?!\w)/ },
  { type: 'italic', pattern: /(?<!\w)_(?=\S)([^_]*?\S)_(?!\w)/ },
];

export function parseMarkdown(text: string): MdNode[] {
  const nodes: MdNode[] = [];
  const blockPattern = /```(\w+)?\n?([\s\S]*?)```/g;
  let last = 0;
  for (const match of text.matchAll(blockPattern)) {
    if (match.index > last) nodes.push(...parseInline(text.slice(last, match.index)));
    nodes.push({
      type: 'codeblock',
      lang: match[1] ?? null,
      text: (match[2] ?? '').replace(/\n$/, ''),
    });
    last = match.index + match[0].length;
  }
  if (last < text.length) nodes.push(...parseInline(text.slice(last)));
  return nodes;
}

function parseInline(text: string): MdNode[] {
  if (!text) return [];
  // Inline code first: its content is literal.
  const code = /`([^`\n]+)`/.exec(text);
  const link = LINK.exec(text);
  const styled = INLINE.map(({ type, pattern }) => ({ type, match: pattern.exec(text) })).filter(
    (c): c is { type: 'bold' | 'strike' | 'italic'; match: RegExpExecArray } => c.match !== null,
  );

  // Pick the earliest match (code and links win ties so their content stays literal).
  const candidates: { index: number; length: number; node: MdNode }[] = [];
  if (code)
    candidates.push({
      index: code.index,
      length: code[0].length,
      node: { type: 'code', text: code[1] ?? '' },
    });
  if (link)
    candidates.push({
      index: link.index,
      length: link[0].length,
      node: { type: 'link', href: link[0], text: link[0] },
    });
  const mention = /<@([0-9a-f-]{36})>/.exec(text);
  if (mention) {
    candidates.push({
      index: mention.index,
      length: mention[0].length,
      node: { type: 'mention', userId: mention[1] ?? null },
    });
  }
  const roleMention = /<@&([0-9a-f-]{36})>/.exec(text);
  if (roleMention) {
    candidates.push({
      index: roleMention.index,
      length: roleMention[0].length,
      node: { type: 'roleMention', roleId: roleMention[1] ?? '' },
    });
  }
  const everyone = /(?<![\p{L}\p{N}_])@canal\b/u.exec(text);
  if (everyone) {
    candidates.push({
      index: everyone.index,
      length: everyone[0].length,
      node: { type: 'mention', userId: null },
    });
  }
  const taskRef = TASK_REF.exec(text);
  if (taskRef) {
    candidates.push({
      index: taskRef.index,
      length: taskRef[0].length,
      node: { type: 'taskRef', key: taskRef[1] ?? '', number: Number(taskRef[2]) },
    });
  }
  for (const { type, match } of styled) {
    candidates.push({
      index: match.index,
      length: match[0].length,
      node: { type, children: parseInline(match[1] ?? '') },
    });
  }
  if (candidates.length === 0) return [{ type: 'text', text }];
  const first = candidates.reduce((a, b) => (b.index < a.index ? b : a));

  return [
    ...(first.index > 0 ? [{ type: 'text' as const, text: text.slice(0, first.index) }] : []),
    first.node,
    ...parseInline(text.slice(first.index + first.length)),
  ];
}
